import { Browser } from '@capacitor/browser';
import type { useClerk } from '@clerk/clerk-react';
import { isNative } from './native';

type Clerk = ReturnType<typeof useClerk>;

/**
 * התחברות עם Google באפליקציה: Google חוסמת OAuth בתוך WebView, ולכן הזרימה היא —
 * 1. פותחים את /native-login של אתר web3 בדפדפן המערכת (Custom Tabs)
 * 2. שם המשתמש מתחבר רגיל (כולל Google), והדף מבקש מ-app3 sign-in token של Clerk
 * 3. הדף מפנה ל-funnermobile://auth?ticket=... → אנדרואיד מחזיר אותנו לאפליקציה
 * 4. כאן פותחים סשן עם ה-ticket (handleNativeAuthUrl)
 */

export const NATIVE_AUTH_SCHEME = 'funnermobile';
export const NATIVE_AUTH_HOST = 'auth';

const WEB_ORIGIN: string = (import.meta.env.VITE_APP_WEB_ORIGIN as string | undefined) || 'https://web3.funner.co.il';
export const NATIVE_LOGIN_URL = `${WEB_ORIGIN}/native-login`;

export const buildNativeAuthReturnUrl = (ticket: string) =>
  `${NATIVE_AUTH_SCHEME}://${NATIVE_AUTH_HOST}?ticket=${encodeURIComponent(ticket)}`;

export const openNativeGoogleSignIn = async () => {
  if (!isNative) return;
  await Browser.open({ url: NATIVE_LOGIN_URL, presentationStyle: 'popover' });
};

export const parseNativeAuthTicket = (url: string): string | null => {
  try {
    const u = new URL(url);
    if (u.protocol !== `${NATIVE_AUTH_SCHEME}:`) return null;
    if (u.host !== NATIVE_AUTH_HOST && u.pathname.replace(/^\/+/, '') !== NATIVE_AUTH_HOST) return null;
    return u.searchParams.get('ticket');
  } catch {
    return null;
  }
};

/** מחזיר true אם ה-URL היה deep link של התחברות וטופל (בהצלחה או בכישלון) */
export const handleNativeAuthUrl = async (url: string, clerk: Clerk): Promise<boolean> => {
  const ticket = parseNativeAuthTicket(url);
  if (!ticket) return false;

  try {
    await Browser.close();
  } catch {
    /* הדפדפן אולי כבר נסגר */
  }

  try {
    const signIn = clerk.client?.signIn;
    if (!signIn) throw new Error('clerk client not ready');
    const result = await signIn.create({ strategy: 'ticket', ticket });
    if (result.status === 'complete' && result.createdSessionId) {
      await clerk.setActive({ session: result.createdSessionId });
    } else {
      console.warn('[native-auth] ticket sign-in incomplete:', result.status);
    }
  } catch (err) {
    console.error('[native-auth] ticket sign-in failed', err);
  }
  return true;
};
